import { useUserPermissions } from "@dashboard/auth/hooks/useUserPermissions";
import { hasPermissions } from "@dashboard/components/RequirePermissions";
import {
  PermissionEnum,
  useAtticApiUsageAppNamesQuery,
  useAtticApiUsageQuery,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { useMemo } from "react";

import AtticApiUsagePage, {
  type AtticApiUsagePageData,
} from "../components/AtticApiUsagePage/AtticApiUsagePage";
import { atticApiUsageUrl, type AtticApiUsageUrlQueryParams } from "../urls";
import {
  getAppName,
  getAppRows,
  getCallerRows,
  getDailyRequests,
  getRangeWindow,
  parseRange,
  sumTotals,
} from "../utils";

/** The API's page ceiling for the name lookups. */
const NAMES_PAGE = 100;

export const AtticApiUsageView = ({ params }: { params: AtticApiUsageUrlQueryParams }) => {
  const navigate = useNavigator();
  const userPermissions = useUserPermissions() ?? [];
  // Names come from the apps and API keys lists, both behind MANAGE_APPS;
  // without it an app is "App #<n>", which says no more than the row does.
  const canListApps = hasPermissions(userPermissions, [PermissionEnum.MANAGE_APPS]);
  const range = parseRange(params.days);
  // Read from the clock whenever the range is chosen (and on every mount), so
  // a page left open past UTC midnight takes in the new day on its next
  // range change instead of ending on yesterday.
  const usageWindow = useMemo(() => getRangeWindow(range, Date.now()), [range]);
  const { data, loading, error } = useAtticApiUsageQuery({
    variables: usageWindow,
    fetchPolicy: "cache-and-network",
  });
  const names = useAtticApiUsageAppNamesQuery({
    variables: { first: NAMES_PAGE },
    skip: !canListApps,
  });
  const rows = data?.atticRequestDailyTotals;
  const pageData = useMemo((): AtticApiUsagePageData | undefined => {
    if (!rows) {
      return undefined;
    }

    const nameSource = names.data
      ? {
          apps: mapEdgesToItems(names.data.apps) ?? [],
          apiKeys: mapEdgesToItems(names.data.atticApiKeys) ?? [],
        }
      : null;
    // While the names are on their way, a name cell is a placeholder rather
    // than "App #<n>" that then swaps for the real name.
    const namesPending = canListApps && names.loading && !names.data;

    return {
      totals: sumTotals(rows),
      callers: getCallerRows(rows),
      apps: getAppRows(rows).map(row => ({
        ...row,
        name: namesPending ? { kind: "loading" as const } : getAppName(row.appId, nameSource),
      })),
      daily: getDailyRequests(rows, usageWindow),
    };
  }, [rows, names.data, names.loading, canListApps, usageWindow]);

  return (
    <AtticApiUsagePage
      range={range}
      data={loading && !rows ? undefined : pageData}
      loadFailed={!!error && !rows}
      onRangeChange={days => navigate(atticApiUsageUrl({ days: String(days) }), { replace: true })}
    />
  );
};

export default AtticApiUsageView;
